import React, { useMemo, useState } from 'react';
import { CheckPicker, Input, Message, Placeholder, SelectPicker, Tag } from 'rsuite';
import { ConfirmModal, FilterField, FilterToolbar, Modal } from '@nsi-advisory-services/components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PermissionGate from '../components/shared/PermissionGate';
import PageHeader from '../components/shared/PageHeader';
import InvitesModal from '../components/employees/InvitesModal';
import PermissionGrantsModal from '../components/employees/PermissionGrantsModal';
import { PERMISSION_OPTIONS, holdsPermission } from '../config/permissions';
import EmployeeForm from '../components/employees/EmployeeForm';
import ProfilePhoto from '../components/employees/ProfilePhoto';
import PermanentDeleteModal from '../components/employees/PermanentDeleteModal';
import PatoReportModal from '../components/payroll/PatoReportModal';
import PatoRosterModal from '../components/payroll/PatoRosterModal';
import EmployeeActionsMenu from '../components/employees/EmployeeActionsMenu';
import CelebrationsModal from '../components/employees/CelebrationsModal';
import { PAYROLL_ENABLED } from '../config/payrollFeature';
import { startImpersonating } from '../utils/impersonation';
import './EmployeesPage.css';
import {
  useCreateEmployee,
  useDeleteEmployee,
  useEmployees,
  useRoles,
  useUpdateEmployee,
} from '../hooks/useQueries';

// Every permission a grants object turns on, as dotted paths.
const listGrantedPermissions = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj).flatMap(([key, flag]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (flag === true) return [path];
    return flag && typeof flag === 'object' ? listGrantedPermissions(flag, path) : [];
  });
};

const formatCommitmentHours = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : null;
};

// Cards per row on wide screens, remembered in this browser.
const COLUMN_OPTIONS = [3, 4, 5, 6].map((n) => ({ label: String(n), value: n }));
const COLUMNS_KEY = 'employees.columns';

const readColumns = () => {
  try {
    const saved = Number(window.localStorage.getItem(COLUMNS_KEY));
    return saved >= 3 && saved <= 6 ? saved : 3;
  } catch {
    return 3;
  }
};

const CLASSIFICATION_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Exempt', value: 'exempt' },
  { label: 'Non-exempt', value: 'non_exempt' },
];

// What the employee is measured against today: a share of TAWH when exempt,
// target hours per pay period when not.
const commitmentLabel = (emp) => {
  if (emp?.is_part_time) {
    const target = emp.target_hours == null || emp.target_hours === '' ? null : formatCommitmentHours(emp.target_hours);
    return target ? `Non-exempt · ${target} hrs target` : 'Non-exempt · no target';
  }
  const pct = emp?.tawh_percent == null || emp.tawh_percent === '' ? 100 : emp.tawh_percent;
  return `${formatCommitmentHours(pct) ?? 100}% commitment`;
};

export default function EmployeesPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const canManageAllEmployees = auth.hasPermission('manage_all_employees');
  const canManageRoles = auth.hasPermission('manage_roles');
  // Not offered while already impersonating: the bar at the top is the way out,
  // and starting another from inside one would only be confusing.
  const canImpersonate = auth.hasPermission('impersonate_employees') && !auth.isImpersonating;
  // The same two permissions the server lets read anybody's PATO.
  const canViewPato = PAYROLL_ENABLED
    && (canManageAllEmployees || auth.hasPermission('manage_payroll'));

  // The same rule the server enforces, checked here so the button can say why
  // rather than fail: nobody can be impersonated who has access you do not.
  const impersonationBlockedReason = (emp) => {
    if (String(emp?.id) === String(auth.employee?.id)) return 'This is you';
    const beyond = listGrantedPermissions(emp?.permission_grants)
      .filter((permission) => !auth.hasPermission(permission));
    return beyond.length > 0 ? `They have permissions you do not: ${beyond.join(', ')}` : null;
  };

  const employeesQuery = useEmployees({ enabled: true });
  const rolesQuery = useRoles({ enabled: canManageAllEmployees });

  const createEmployee = useCreateEmployee();
  const deleteEmployee = useDeleteEmployee();
  const updateEmployee = useUpdateEmployee();

  const employees = useMemo(() => employeesQuery.data || [], [employeesQuery.data]);
  const roles = rolesQuery.data || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [classification, setClassification] = useState('all');
  const [permissionFilter, setPermissionFilter] = useState([]);
  const [columns, setColumns] = useState(readColumns);

  const changeColumns = (value) => {
    const next = value || 3;
    setColumns(next);
    try {
      window.localStorage.setItem(COLUMNS_KEY, String(next));
    } catch {
      // Not remembered; the choice still applies now.
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtersActive = Boolean(normalizedSearch) || classification !== 'all' || permissionFilter.length > 0;

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const aFirst = String(a?.first_name || '').toLowerCase();
      const bFirst = String(b?.first_name || '').toLowerCase();
      if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

      const aLast = String(a?.last_name || '').toLowerCase();
      const bLast = String(b?.last_name || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);

      return String(a?.email || '').toLowerCase().localeCompare(String(b?.email || '').toLowerCase());
    });
  }, [employees]);

  // Non-exempt is stored as is_part_time. A permission filter shows anyone
  // holding any of the picked permissions (Administrator holds them all).
  const filteredEmployees = useMemo(() => sortedEmployees.filter((e) => {
    if (classification !== 'all' && Boolean(e?.is_part_time) !== (classification === 'non_exempt')) return false;
    if (permissionFilter.length && !permissionFilter.some((key) => holdsPermission(e?.permission_grants, key))) return false;
    if (!normalizedSearch) return true;
    const fullName = `${e?.first_name || ''} ${e?.last_name || ''}`.trim().toLowerCase();
    return fullName.includes(normalizedSearch) || String(e?.email || '').toLowerCase().includes(normalizedSearch);
  }), [sortedEmployees, classification, permissionFilter, normalizedSearch]);

  const activeEmployees = filteredEmployees.filter((e) => e?.is_active !== false);
  const inactiveEmployees = filteredEmployees.filter((e) => e?.is_active === false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [confirmReactivate, setConfirmReactivate] = useState(null);
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState(null);
  const [patoReportEmployee, setPatoReportEmployee] = useState(null);
  const [patoRosterOpen, setPatoRosterOpen] = useState(false);
  const [celebrationsOpen, setCelebrationsOpen] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [permissionsTarget, setPermissionsTarget] = useState(null);

  const handleCreate = () => {
    setEditorOpen(true);
  };

  const handleEdit = (row) => {
    if (!row?.id) return;
    navigate(`/admin/employees/${row.id}/edit`);
  };

  const handleView = (row) => {
    if (!row?.id) return;
    navigate(`/admin/employees/${row.id}/edit`);
  };

  const handleViewResume = (row) => {
    if (!row?.id) return;
    navigate(`/admin/employees/${row.id}/resume`);
  };

  const handleDeactivate = (row) => {
    setConfirmDeactivate(row);
  };

  const handleReactivate = (row) => {
    setConfirmReactivate(row);
  };

  const submitEmployee = async (payload) => {
    await createEmployee.mutateAsync(payload);
    setEditorOpen(false);
  };

  const doDeactivate = async () => {
    if (!confirmDeactivate?.id) return;
    await deleteEmployee.mutateAsync(confirmDeactivate.id);
    setConfirmDeactivate(null);
  };

  const doReactivate = async () => {
    if (!confirmReactivate?.id) return;
    try {
      await updateEmployee.mutateAsync({ id: confirmReactivate.id, is_active: true });
      setConfirmReactivate(null);
    } catch {
      // Shown in the dialog (no free seat, for one).
    }
  };

  if (employeesQuery.isLoading) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (employeesQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(employeesQuery.error?.message || employeesQuery.error)}
      </Message>
    );
  }

  const renderEmployeeGrid = (list, { inactive = false } = {}) => (
    <div className={`row row-cols-1 row-cols-md-2 row-cols-xl-${columns} g-3`}>
      {(list || []).map((emp) => {
        const initials = `${emp?.first_name?.[0] || ''}${emp?.last_name?.[0] || ''}`.toUpperCase() || 'U';

        return (
          <div className="col" key={emp.id}>
            <div
              className="h-100 employee-card employee-card--clickable"
              role="button"
              tabIndex={0}
              onClick={() => handleView(emp)}
              onKeyDown={(e) => {
                // Only when the card itself has focus. Otherwise Enter on a
                // control inside it bubbles up here and opens the employee
                // instead of doing what the control does.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleView(emp);
                }
              }}
            >
              <div className="employee-card__head">
                <div className="employee-card__avatar">
                  <ProfilePhoto employeeId={emp.id} size="small" fallbackInitials={initials} />
                </div>

                <div className="employee-card__text">
                  <div className="employee-card__name">
                    {emp.first_name} {emp.last_name}
                  </div>
                  <div className="employee-card__email" title={emp.email}>
                    {emp.email}
                  </div>
                </div>

                {/* Top right: the commitment in force today (the employee
                    record mirrors it from the dated history), and under it the
                    role - shown on every card; for those who can assign roles,
                    the pill itself is the way to change it. */}
                <div className="employee-card__side">
                  {inactive ? (
                    <Tag color="red" size="sm">Deactivated</Tag>
                  ) : (
                    <span className="employee-card__badge" title="Current commitment">
                      {commitmentLabel(emp)}
                    </span>
                  )}
                  {emp.role_name ? (
                    canManageRoles ? (
                      <button
                        type="button"
                        className="employee-card__role employee-card__role--action"
                        title="Edit permissions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPermissionsTarget(emp);
                        }}
                      >
                        {emp.role_name}
                        <i className="bi bi-chevron-down" aria-hidden="true" />
                      </button>
                    ) : (
                      <span className="employee-card__role">{emp.role_name}</span>
                    )
                  ) : null}
                </div>
              </div>

              {/* The same row on every card: the everyday actions as text,
                  split by hairlines, and the admin-only ones behind the menu
                  at the end. */}
              <div className="employee-card__actions">
                {canViewPato ? (
                  <button
                    type="button"
                    className="employee-card__action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPatoReportEmployee(emp);
                    }}
                  >
                    PATO report
                  </button>
                ) : null}
                <button
                  type="button"
                  className="employee-card__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewResume(emp);
                  }}
                >
                  Resume
                </button>
                <button
                  type="button"
                  className="employee-card__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(emp);
                  }}
                >
                  Edit
                </button>

                <EmployeeActionsMenu
                  label={`More actions for ${emp.first_name} ${emp.last_name}`}
                  triggerClassName="employee-card__action employee-card__more"
                  items={[
                    canImpersonate && !inactive && {
                      key: 'impersonate',
                      label: 'Impersonate',
                      icon: 'bi-person-badge',
                      disabled: Boolean(impersonationBlockedReason(emp)),
                      hint: impersonationBlockedReason(emp),
                      onSelect: () => startImpersonating(emp.id),
                    },
                    canManageRoles && {
                      key: 'permissions',
                      label: 'Permissions',
                      icon: 'bi-shield-lock',
                      onSelect: () => setPermissionsTarget(emp),
                    },
                    canManageAllEmployees && inactive && {
                      key: 'reactivate',
                      label: 'Reactivate',
                      icon: 'bi-arrow-counterclockwise',
                      onSelect: () => handleReactivate(emp),
                    },
                    canManageAllEmployees && !inactive && {
                      key: 'deactivate',
                      label: 'Deactivate',
                      icon: 'bi-person-dash',
                      danger: true,
                      onSelect: () => handleDeactivate(emp),
                    },
                    // Permanent deletion is only offered once an employee is
                    // already deactivated, so the reversible step always comes
                    // first. The server enforces this too.
                    canManageAllEmployees && inactive && {
                      key: 'delete',
                      label: 'Delete permanently',
                      icon: 'bi-trash',
                      danger: true,
                      onSelect: () => setConfirmPermanentDelete(emp),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Employees' },
        ]}
        icon="bi-people"
        title="Employee Management"
        count={filtersActive
          ? `${filteredEmployees.length} of ${employees.length}`
          : employees.length}
        actions={(
          <>
            {canManageAllEmployees ? (
              <button type="button" className="bdc-btn" onClick={() => setInvitesOpen(true)}>
                <i className="bi bi-envelope-plus" aria-hidden="true" />
                Invites
              </button>
            ) : null}

            {canManageAllEmployees ? (
              <button type="button" className="bdc-btn" onClick={() => setCelebrationsOpen(true)}>
                <i className="bi bi-cake2" aria-hidden="true" />
                Celebrations
              </button>
            ) : null}

            {canViewPato ? (
              <button type="button" className="bdc-btn" onClick={() => setPatoRosterOpen(true)}>
                <i className="bi bi-graph-down" aria-hidden="true" />
                PATO report
              </button>
            ) : null}

            <PermissionGate permission="manage_all_employees" fallback={null}>
              <button type="button" className="bdc-btn bdc-btn--primary" onClick={handleCreate}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                New Employee
              </button>
            </PermissionGate>
          </>
        )}
      />

      <FilterToolbar label="Employee filters">
        <FilterField label="Search" icon="bi-search" basis={320}>
          <Input
            type="search"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Name or email"
            aria-label="Search employees"
          />
        </FilterField>
        <FilterField label="Classification" basis={180}>
          <SelectPicker
            data={CLASSIFICATION_OPTIONS}
            value={classification}
            onChange={(value) => setClassification(value || 'all')}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="Permissions" basis={280}>
          <CheckPicker
            data={PERMISSION_OPTIONS}
            value={permissionFilter}
            onChange={(value) => setPermissionFilter(value || [])}
            placeholder="Any permissions"
            block
          />
        </FilterField>
        <FilterField label="Columns" basis={110} pinned>
          <SelectPicker
            data={COLUMN_OPTIONS}
            value={columns}
            onChange={changeColumns}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
      </FilterToolbar>

      {filtersActive && filteredEmployees.length === 0 ? (
        <Message type="info" showIcon>
          No employees match these filters.
        </Message>
      ) : null}

      {renderEmployeeGrid(activeEmployees, { inactive: false })}

      {inactiveEmployees.length > 0 ? (
        <details className="employee-inactive-section">
          <summary className="employee-inactive-summary">
            Inactive Employees ({inactiveEmployees.length})
          </summary>
          <div className="mt-3">
            {renderEmployeeGrid(inactiveEmployees, { inactive: true })}
          </div>
        </details>
      ) : null}

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>
            Create Employee
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EmployeeForm
            initialValue={null}
            roles={roles}
            onSubmit={submitEmployee}
            submitLabel="Create"
            loading={createEmployee.isPending}
            error={createEmployee.error}
          />
        </Modal.Body>
      </Modal>

      <ConfirmModal
        open={!!confirmDeactivate}
        title="Deactivate Employee"
        body={`Deactivate ${confirmDeactivate?.first_name} ${confirmDeactivate?.last_name}? They will no longer be able to sign in.`}
        confirmText="Deactivate"
        danger
        loading={deleteEmployee.isPending}
        onConfirm={doDeactivate}
        onCancel={() => setConfirmDeactivate(null)}
      />

      <PatoRosterModal open={patoRosterOpen} onClose={() => setPatoRosterOpen(false)} />

      <CelebrationsModal open={celebrationsOpen} onClose={() => setCelebrationsOpen(false)} />

      <InvitesModal open={invitesOpen} onClose={() => setInvitesOpen(false)} />

      <PermissionGrantsModal employee={permissionsTarget} onClose={() => setPermissionsTarget(null)} />

      <PatoReportModal
        open={Boolean(patoReportEmployee)}
        onClose={() => setPatoReportEmployee(null)}
        employee={patoReportEmployee}
      />

      <PermanentDeleteModal
        employee={confirmPermanentDelete}
        onClose={() => setConfirmPermanentDelete(null)}
        onDeleted={() => setConfirmPermanentDelete(null)}
      />

      <ConfirmModal
        open={!!confirmReactivate}
        title="Reactivate Employee"
        body={(
          <>
            Reactivate {confirmReactivate?.first_name} {confirmReactivate?.last_name}? They will be able to sign in again.
            {updateEmployee.error ? (
              <Message type="error" showIcon className="mt-3">
                {String(updateEmployee.error?.message || updateEmployee.error)}
              </Message>
            ) : null}
          </>
        )}
        confirmText="Reactivate"
        loading={updateEmployee.isPending}
        onConfirm={doReactivate}
        onCancel={() => setConfirmReactivate(null)}
        onExited={() => updateEmployee.reset()}
      />
    </div>
  );
}
